import React, { useState } from 'react'
import { Button } from '../../components/ui/button'
import {
    customFieldApiError,
    customFieldsApi,
    isCustomFieldValueSet,
    type CustomFieldValue,
} from './api'
import { CUSTOM_FIELD_DATA_TYPES } from './types'
import type { CustomFieldDefinition, CustomFieldEntityType } from './types'
import { validateCustomFieldInput } from './ValueEditor'

const inputClass = 'w-32 rounded border border-gray-300 px-2 py-1 text-sm'

export const formatCustomFieldValue = (
    definition: Pick<CustomFieldDefinition, 'data_type'>,
    value: string | boolean | null | undefined,
): string => {
    if (value === null || value === undefined || value === '') return 'Not set'
    if (definition.data_type === CUSTOM_FIELD_DATA_TYPES.Boolean) return value ? 'Yes' : 'No'
    return String(value)
}

export interface CustomFieldValueCellProps {
    profileId: string
    entityType: CustomFieldEntityType
    entityId: string
    definition: CustomFieldDefinition
    current?: CustomFieldValue
    valueQuotaReached: boolean
    onChanged: (nextValue: CustomFieldValue | undefined) => void
}

/**
 * Click-to-edit cell: shows the current value as a chip; clicking reveals an
 * inline input plus explicit Set/Clear/Cancel, matching the interaction
 * already proven in the ad-manager tables' CustomFieldCell (no autosave, no
 * modal). Shared here so grid-style surfaces don't reimplement it.
 */
export const CustomFieldValueCell = ({
    profileId,
    entityType,
    entityId,
    definition,
    current,
    valueQuotaReached,
    onChanged,
}: CustomFieldValueCellProps) => {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState<string | boolean | null>(current?.value ?? null)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const startEditing = () => {
        setDraft(current?.value ?? null)
        setError(null)
        setEditing(true)
    }

    const mutate = async (clear: boolean) => {
        const validation = clear ? null : validateCustomFieldInput(definition, draft)
        if (validation) return setError(validation)
        setSaving(true)
        setError(null)
        try {
            const result = await customFieldsApi.mutateValues(profileId, entityType, [
                {
                    field_definition_id: definition.definition_id,
                    entity_id: entityId,
                    operation: clear ? 'clear' : 'set',
                    ...(!clear ? { value: draft as string | boolean } : {}),
                    expected_version: current?.version ?? null,
                },
            ])
            const updated = result.values[0]
            onChanged(updated && isCustomFieldValueSet(updated) ? updated : undefined)
            setEditing(false)
        } catch (requestError) {
            setError(customFieldApiError(requestError))
        } finally {
            setSaving(false)
        }
    }

    if (!editing) {
        const isSet = current?.value !== undefined && current?.value !== null
        return (
            <button
                type="button"
                aria-label={`Edit ${definition.name}`}
                onClick={startEditing}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                    isSet
                        ? 'border-[#c9e2ff] bg-[#eef4fd] text-[#0e2947]'
                        : 'border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600'
                }`}
            >
                {formatCustomFieldValue(definition, current?.value)}
            </button>
        )
    }

    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
                {definition.data_type === CUSTOM_FIELD_DATA_TYPES.Boolean ? (
                    <select
                        autoFocus
                        className={inputClass}
                        value={draft === null ? '' : draft ? 'true' : 'false'}
                        onChange={(event) =>
                            setDraft(
                                event.target.value === '' ? null : event.target.value === 'true',
                            )
                        }
                    >
                        <option value="">Not set</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                ) : (
                    <input
                        autoFocus
                        aria-label={`${definition.name} value`}
                        className={inputClass}
                        value={typeof draft === 'string' ? draft : ''}
                        onChange={(event) => setDraft(event.target.value)}
                        maxLength={
                            definition.data_type === CUSTOM_FIELD_DATA_TYPES.String
                                ? 250
                                : undefined
                        }
                        placeholder={
                            definition.data_type === CUSTOM_FIELD_DATA_TYPES.Number
                                ? '0.00'
                                : 'Value'
                        }
                    />
                )}
                <Button
                    size="sm"
                    disabled={saving || draft === null || (valueQuotaReached && !current)}
                    onClick={() => mutate(false)}
                >
                    Set
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    disabled={saving || !current}
                    onClick={() => mutate(true)}
                >
                    Clear
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    disabled={saving}
                    onClick={() => setEditing(false)}
                >
                    Cancel
                </Button>
            </div>
            {error && <span className="text-xs text-red-600">{error}</span>}
        </div>
    )
}
